import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors - only redirect for protected pages
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem('auth_token');
      // Only redirect to login if user had a token (session expired) not if browsing publicly
      if (token) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const verifyToken = () => api.get('/auth/verify');
export const getCurrentUser = () => api.get('/auth/me');

// User Management (Admin)
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const updateUserProfile = (data) => api.put('/users/profile', data);

// Verticals
export const getVerticals = () => api.get('/verticals');
export const getAllVerticalsAdmin = () => api.get('/verticals/all');
export const getVertical = (id) => api.get(`/verticals/${id}`);
export const createVertical = (data) => api.post('/verticals', data);
export const updateVertical = (id, data) => api.put(`/verticals/${id}`, data);
export const deleteVertical = (id) => api.delete(`/verticals/${id}`);
export const importVerticalData = (verticalId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/admin/verticals/${verticalId}/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Video Testimonials Gallery
export const getVideoTestimonials = () => api.get('/video-testimonials');

// Case Studies
export const getCaseStudies = (verticalId) => api.get(verticalId ? `/case-studies?vertical_id=${verticalId}` : '/case-studies');
export const getCaseStudy = (id) => api.get(`/case-studies/${id}`);
export const createCaseStudy = (data) => api.post('/case-studies', data);
export const updateCaseStudy = (id, data) => api.put(`/case-studies/${id}`, data);
export const deleteCaseStudy = (id) => api.delete(`/case-studies/${id}`);
export const uploadCaseStudyPdf = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/case-studies/${id}/upload-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Problems
export const getProblems = () => api.get('/problems');
export const getProblemsByVertical = (verticalId) => api.get(`/problems/vertical/${verticalId}`);
export const getProblem = (id) => api.get(`/problems/${id}`);
export const createProblem = (data) => api.post('/problems', data);
export const updateProblem = (id, data) => api.put(`/problems/${id}`, data);
export const deleteProblem = (id) => api.delete(`/problems/${id}`);

// Icon Suggestion
export const suggestIcon = (title, description) => api.post('/suggest-icon', { title, description });

// Solutions
export const getSolutionByProblem = (problemId) => api.get(`/solutions/problem/${problemId}`);
export const createSolution = (data) => api.post('/solutions', data);
export const updateSolution = (id, data) => api.put(`/solutions/${id}`, data);
export const deleteSolution = (id) => api.delete(`/solutions/${id}`);

// Client Logos (legacy)
export const getLogos = () => api.get('/logos');
export const getLogosByVertical = (verticalId) => api.get(`/logos/vertical/${verticalId}`);
export const createLogo = (data) => api.post('/logos', data);
export const deleteLogo = (id) => api.delete(`/logos/${id}`);

// Client Master (full profiles)
export const getClients = () => api.get('/clients');
export const getClientsByVertical = (verticalId) => api.get(`/clients/vertical/${verticalId}`);
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Branches
export const getBranches = () => api.get('/branches');
export const createBranch = (data) => api.post('/branches', data);
export const updateBranch = (id, data) => api.put(`/branches/${id}`, data);
export const deleteBranch = (id) => api.delete(`/branches/${id}`);

// Technical Experts
export const getExperts = () => api.get('/experts');
export const createExpert = (data) => api.post('/experts', data);
export const deleteExpert = (id) => api.delete(`/experts/${id}`);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getDashboardSettings = () => api.get('/dashboard/settings');
export const updateDashboardSettings = (data) => api.put('/dashboard/settings', data);

// AI Content Rewriter
export const aiRewrite = (text, style = 'humanized', language = 'en') => 
  api.post('/ai-rewrite', { text, style, language });

// Contact
export const getContactInfo = () => api.get('/contact');
export const updateContactInfo = (data) => api.put('/contact', data);

// AI Consultant Knowledge Base
export const getAIDocuments = () => api.get('/ai-documents');
export const uploadAIDocument = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/ai-documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteAIDocument = (id) => api.delete(`/ai-documents/${id}`);

// AI Consultant Videos
export const getAIConsultantVideos = () => api.get('/ai-consultant-videos');
export const createAIConsultantVideo = (data) => api.post('/ai-consultant-videos', data);
export const deleteAIConsultantVideo = (id) => api.delete(`/ai-consultant-videos/${id}`);

// AI Search (RAG Consultant) - P1: Updated with session and vertical support
export const aiSearch = (query, conversationHistory = [], sessionId = '', verticalId = null, language = 'en') => 
  api.post('/ai-search', { 
    query, 
    conversation_history: conversationHistory,
    session_id: sessionId,
    vertical_id: verticalId,
    language: language
  });

// AI Sales Knowledge Base
export const getAISalesDocuments = () => api.get('/ai-sales-documents');
export const uploadAISalesDocument = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/ai-sales-documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteAISalesDocument = (id) => api.delete(`/ai-sales-documents/${id}`);

// AI Sales Videos
export const getAISalesVideos = () => api.get('/ai-sales-videos');
export const createAISalesVideo = (data) => api.post('/ai-sales-videos', data);
export const deleteAISalesVideo = (id) => api.delete(`/ai-sales-videos/${id}`);

// AI Sales Search (RAG Sales Coach)
export const aiSalesSearch = (query, conversationHistory = [], prospectStage = 'initial') => 
  api.post('/ai-sales-search', { query, conversation_history: conversationHistory, prospect_stage: prospectStage });

// Legacy Search (keyword-based)
export const search = (query) => api.post('/search', { query });

// Video Upload
export const uploadVideo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/video', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Seed
export const seedData = () => api.post('/seed');
export const clearData = () => api.post('/clear-data');

// Audit Logs
export const createAuditLog = (data) => api.post('/audit-log', data);
export const getAuditLogs = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.user_id) params.append('user_id', filters.user_id);
  if (filters.action) params.append('action', filters.action);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.company) params.append('company', filters.company);
  return api.get(`/audit-logs?${params.toString()}`);
};
export const getUserBehaviorSummary = (userId) => api.get(`/audit-logs/user-summary/${userId}`);
export const exportAuditLogs = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  return api.get(`/audit-logs/export?${params.toString()}`, { responseType: 'blob' });
};

// Leads
export const getLeads = (status) => api.get(status ? `/leads?status=${status}` : '/leads');
export const createLeadAdmin = (data) => api.post('/leads/admin', data);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);
export const searchLeads = (query) => api.get(`/leads/search?q=${encodeURIComponent(query)}`);
export const createLeadInternal = (data) => api.post('/leads/internal-create', data);
export const getBookingSubmissions = (source) => api.get(source && source !== 'all' ? `/leads/bookings?source=${source}` : '/leads/bookings');

// YouTube Transcript Extraction
export const extractYouTubeTranscript = (data) => api.post('/youtube-transcript', data);
export const getYouTubeTranscripts = (targetKb = 'ai_consultant') => 
  api.get(`/youtube-transcripts?target_kb=${targetKb}`);
export const deleteYouTubeTranscript = (docId, targetKb = 'ai_consultant') => 
  api.delete(`/youtube-transcript/${docId}?target_kb=${targetKb}`);

// JSON Dataset Upload
export const getDatasets = () => api.get('/datasets');
export const uploadDataset = (formData) => api.post('/datasets/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const uploadTextDataset = (formData) => api.post('/datasets/upload-text', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteDataset = (filename) => api.delete(`/datasets/${filename}`);

// AI Consultant Reindex & Training
export const reindexAIConsultant = () => api.post('/ai-consultant/reindex');
export const getIndexedDatasets = (verticalId = null, datasetType = null) => {
  const params = new URLSearchParams();
  if (verticalId) params.append('vertical_id', verticalId);
  if (datasetType) params.append('dataset_type', datasetType);
  return api.get(`/ai-consultant/indexed-datasets?${params.toString()}`);
};
export const deleteIndexedDataset = (datasetId) => api.delete(`/ai-consultant/indexed-datasets/${datasetId}`);
export const trainAIConsultant = () => api.post('/ai-consultant/train');
export const getTrainingHistory = () => api.get('/ai-consultant/training-history');

// P2: Chat Memory Testing
export const testChatMemory = () => api.post('/ai-consultant/test-memory');
export const getChatSession = (sessionId) => api.get(`/ai-consultant/session/${sessionId}`);
export const getUserChatSessions = () => api.get('/ai-consultant/sessions');

// Lead Capture
export const submitLead = (data) => api.post('/leads', data);

// P2: Advanced Analytics
export const getAnalyticsSummary = () => api.get('/analytics/summary');
export const getLeadFunnel = () => api.get('/analytics/lead-funnel');
export const getEngagementTimeline = (days = 7) => api.get(`/analytics/engagement-timeline?days=${days}`);
export const getVerticalPerformance = () => api.get('/analytics/vertical-performance');
export const getChatSessionsAnalytics = () => api.get('/analytics/chat-sessions');

// Bulk Data Import/Export
export const getTemplates = () => api.get('/admin/templates');
export const downloadTemplate = (templateType) => {
  return api.get(`/admin/templates/${templateType}`, { responseType: 'blob' });
};
export const downloadWordTemplate = () => {
  return api.get('/admin/templates/vertical-word', { responseType: 'blob' });
};
export const bulkImportVerticals = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/bulk-import/verticals', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const bulkImportVerticalsDocx = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/bulk-import/verticals-docx', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const bulkImportProblems = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/bulk-import/problems', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const exportVerticals = () => api.get('/admin/export/verticals', { responseType: 'blob' });
export const exportProblems = () => api.get('/admin/export/problems', { responseType: 'blob' });
export const exportVerticalsProblemsExcel = () => api.get('/admin/export/verticals-problems-excel', { responseType: 'blob' });

// ROI Calculator
export const calculateROI = (data) => api.post('/roi-calculator/calculate', data);
export const generateROIPDF = (data) => api.post('/roi-calculator/generate-pdf', data, { responseType: 'blob' });
export const getVerticalROIMetrics = (verticalId) => api.get(`/roi-calculator/vertical/${verticalId}`);

// Assessment
export const getAssessmentQuestions = () => api.get('/assessment/questions');
export const submitAssessment = (data) => api.post('/assessment/submit', data);
export const getAssessmentResult = (id) => api.get(`/assessment/${id}`);
export const getAssessmentHistory = (email) => api.get(`/assessment/history/${encodeURIComponent(email)}`);
export const downloadAssessmentPDF = (id) => api.get(`/assessment/${id}/pdf`, { responseType: 'blob' });
export const getAdminAssessments = () => api.get('/admin/assessments');
export const deleteAssessment = (id) => api.delete(`/admin/assessments/${id}`);

// CODN Calculator
export const calculateCODN = (data) => api.post('/codn/calculate', data);
export const getCODNResult = (id) => api.get(`/codn/${id}`);
export const getCODNHistory = (email) => api.get(`/codn/history/${encodeURIComponent(email)}`);
export const downloadCODNPDF = (id) => api.get(`/codn/${id}/pdf`, { responseType: 'blob' });
export const getAdminCODN = () => api.get('/admin/codn');
export const deleteCODN = (id) => api.delete(`/admin/codn/${id}`);

// System Landscape Mapper
export const getToolLibrary = () => api.get('/system-mapper/tool-library');
export const submitSystemMapper = (data) => api.post('/system-mapper/submit', data);
export const getSystemMapperResult = (id) => api.get(`/system-mapper/${id}`);
export const getSystemMapperHistory = (email) => api.get(`/system-mapper/history/${encodeURIComponent(email)}`);
export const downloadSystemMapperPDF = (id) => api.get(`/system-mapper/${id}/pdf`, { responseType: 'blob' });
export const getAdminSystemMappers = () => api.get('/admin/system-mapper');
export const deleteSystemMapper = (id) => api.delete(`/admin/system-mapper/${id}`);

// AI Tools Status
export const getAIToolsStatus = () => api.get('/ai-tools/status');

// Resources
export const getResources = (params) => api.get('/resources', { params });
export const getAdminResources = () => api.get('/admin/resources');
export const createResource = (data) => api.post('/admin/resources', data);
export const updateResource = (id, data) => api.put(`/admin/resources/${id}`, data);
export const deleteResource = (id) => api.delete(`/admin/resources/${id}`);

// AI Configuration (Admin)
export const getAIConfig = () => api.get('/admin/ai-config');
export const updateAIConfig = (data) => api.put('/admin/ai-config', data);
export const testAIConfig = () => api.post('/admin/ai-config/test');

// Credits Management
export const getAllUserCredits = () => api.get('/admin/users/credits');
export const updateUserCredits = (userId, credits) => api.put(`/admin/users/${userId}/credits`, { credits });
export const bulkUpdateCredits = (defaultCredits) => api.post('/admin/users/credits/bulk', { default_credits: defaultCredits });
export const getMyCredits = () => api.get('/user/credits');

// Feedback Management (Campaign-based)
// Public form endpoints don't need auth — handled directly in FeedbackFormPublic.jsx
// Admin endpoints use the default api instance with auth

export default api;
